import React, { useEffect, useState } from 'react';
import { Box, Grid, Card, CardMedia, CardContent, Typography, Chip, Pagination, CircularProgress, Stack } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { createConcurrencyLimiter } from '../../utils/concurrentLimit';
import {
  CameraUploadImage,
  getCameraUploadFolderImages,
  getCameraUploadThumbnailUrl,
  fetchCameraUploadImageBlob,
} from '../../api/cameraUpload';

export const PAGE_SIZE = 100;
const THUMBNAIL_SIZE = 100;

interface ImageThumbnailGridProps {
  deviceId: string;
  className: string;
  refreshToken: number;
  onImageClick: (image: CameraUploadImage) => void;
  page: number;
  onPageChange: (page: number) => void;
  onImagesChange: (images: CameraUploadImage[], totalCount: number) => void;
}

const ImageThumbnailGrid: React.FC<ImageThumbnailGridProps> = ({ deviceId, className, refreshToken, onImageClick, page, onPageChange, onImagesChange }) => {
  const auth = useAuth();
  const accessToken = auth.tokens?.accessToken ?? '';
  const [images, setImages] = useState<CameraUploadImage[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [thumbnails, setThumbnails] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getCameraUploadFolderImages(deviceId, className, page, PAGE_SIZE, accessToken)
      .then((result) => {
        if (cancelled) return;
        setImages(result.images);
        setTotalCount(result.totalCount);
        onImagesChange(result.images, result.totalCount);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId, className, page, refreshToken]);

  useEffect(() => {
    if (images.length === 0) return;
    setThumbnails((prev) => {
      Object.values(prev).forEach((url) => URL.revokeObjectURL(url));
      return {};
    });

    let cancelled = false;
    const limit = createConcurrencyLimiter(6);
    Promise.allSettled(
      images.map((image) =>
        limit(async () => {
          if (cancelled) return;
          const url = await fetchCameraUploadImageBlob(getCameraUploadThumbnailUrl(deviceId, image.id, THUMBNAIL_SIZE), accessToken);
          if (cancelled) return;
          setThumbnails((prev) => ({ ...prev, [image.id]: url }));
        })
      )
    );

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images]);

  useEffect(() => {
    return () => {
      setThumbnails((prev) => {
        Object.values(prev).forEach((url) => URL.revokeObjectURL(url));
        return {};
      });
    };
  }, []);

  if (loading && images.length === 0) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }

  if (images.length === 0) {
    return <Typography color="text.secondary">No images in this folder.</Typography>;
  }

  return (
    <Box>
      <Grid container spacing={1}>
        {images.map((image) => (
          <Grid item xs={4} sm={3} md={2} lg={1} key={image.id}>
            <Card sx={{ cursor: 'pointer' }} onClick={() => onImageClick(image)}>
              <CardMedia
                component="img"
                image={thumbnails[image.id]}
                sx={{ aspectRatio: '1', objectFit: 'cover', bgcolor: 'action.hover', width: THUMBNAIL_SIZE, height: THUMBNAIL_SIZE, mx: 'auto' }}
              />
              <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                <Typography variant="caption" noWrap display="block">{image.originalFileName}</Typography>
                <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                  {image.classifications.map((c) => (
                    <Chip
                      key={c.id}
                      size="small"
                      label={c.confidence != null ? `${c.className} ${Math.round(c.confidence * 100)}%` : c.className}
                      color={c.source === 'Manual' ? 'primary' : 'default'}
                      variant={c.source === 'Manual' ? 'filled' : 'outlined'}
                    />
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      {totalCount > PAGE_SIZE && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            count={Math.ceil(totalCount / PAGE_SIZE)}
            page={page}
            onChange={(_e, value) => onPageChange(value)}
          />
        </Box>
      )}
    </Box>
  );
};

export default ImageThumbnailGrid;
